/**
 * `v-collab` — a live text box (lib/collabText.ts) for one field of a saved
 * task, where several people type at once and see each other's carets.
 *
 * A draft has nothing on the server to share yet, so with `live: false` this
 * is `v-synced` exactly, and the field commits on `change` as before. The
 * switch from one to the other (a draft saved, another task opened) is a new
 * `key` or `live`, which closes the old session and opens the new one.
 *
 *   <textarea v-collab="{ key: task.id, field: 'notes', value: task.notes, live: !unsaved }" />
 */
import type { Directive, DirectiveBinding } from "vue";
import type { CollabField } from "@shared/types.js";
import { useBoard } from "../stores/board.js";
import type { CollabText } from "./collabText.js";
import { vSynced } from "./syncedValue.js";

type Field = HTMLInputElement | HTMLTextAreaElement;

interface Binding {
  key: string;
  field: CollabField;
  value: string | null | undefined;
  live: boolean;
  /** Called after someone else's typing changed the box (a title refits itself). */
  onRemote?: () => void;
}

const sessions = new WeakMap<Field, CollabText>();

function open(el: Field, value: Binding, adopt = false): void {
  sessions.set(el, useBoard().openLiveText(el, value.key, value.field, value.value ?? "", value.onRemote, adopt));
}

function close(el: Field): void {
  const live = sessions.get(el);
  if (!live) return;
  sessions.delete(el);
  useBoard().closeLiveText(live);
}

/** v-synced's own binding, for the draft case. */
const synced = (b: DirectiveBinding<Binding>): DirectiveBinding<{ key: string; value: string | null | undefined }> =>
  ({ ...b, value: { key: b.value.key, value: b.value.value }, oldValue: null }) as never;

export const vCollab: Directive<Field, Binding> = {
  mounted(el, binding) {
    if (binding.value.live) open(el, binding.value);
    else vSynced.mounted!(el, synced(binding) as never, null as never, null);
  },
  updated(el, binding) {
    const old = binding.oldValue;
    const now = binding.value;
    const same = old && old.key === now.key && old.field === now.field && old.live === now.live;
    if (same && now.live) return sessions.get(el)?.serverValue(now.value ?? "");
    if (!same) close(el);
    // A draft that has just been saved, typed in still: what is in the box is the reader's.
    if (now.live) open(el, now, old?.live === false && document.activeElement === el);
    else vSynced.updated!(el, synced(binding) as never, null as never, null as never);
  },
  beforeUnmount(el) {
    close(el);
  },
};
